'use client';

import React from 'react';
import Link from 'next/link';
import { useRouter } from 'next/navigation';
import AppImage from '@/components/ui/AppImage';

const services = [
{
  id: 'claim',
  label: 'Non-Fault Claims',
  title: 'Zero-cost repairs when it wasn\'t your fault',
  body: 'We manage the entire insurance claim on your behalf. Our claims partner deals directly with the at-fault driver\'s insurer. Your excess: £0. Your stress: none.',
  tag: 'Most Popular',
  tagColor: '#C8A96E',
  wide: true,
  tall: false,
  dark: true,
  img: "https://img.rocket.new/generatedImages/rocket_gen_img_1062535a5-1773009637541.png",
  imgAlt: 'Car body technician inspecting accident damage on a white SUV in a professional workshop',
  href: '/how-non-fault-claims-work',
  buttonHref: '/how-non-fault-claims-work',
},
{
  id: 'structural',
  label: 'Structural Repair',
  title: 'Car-O-Liner precision chassis alignment',
  body: 'Our Car-O-Liner bench restores your vehicle\'s geometry to within ±0.5mm of factory spec — critical for safety and correct panel fit.',
  tag: 'Specialist Equipment',
  tagColor: '#4A7CFF',
  wide: false,
  tall: true,
  dark: false,
  img: "https://img.rocket.new/generatedImages/rocket_gen_img_12445e72e-1773009637750.png",
  imgAlt: 'Car frame on a Car-O-Liner structural repair bench with precision measurement tools attached',
  href: '/services/chassis-alignment',
  buttonHref: '/services/chassis-alignment#booking-form',
},
{
  id: 'airbag',
  label: 'Airbag & SRS',
  title: 'Airbag replacement & SRS system reset',
  body: 'Full airbag module replacement and SRS ECU reset to manufacturer specification. Safety-critical work done right.',
  tag: 'Safety Critical',
  tagColor: '#E84545',
  wide: false,
  tall: false,
  dark: false,
  img: null,
  imgAlt: null,
  href: '/services/airbag-srs',
  buttonHref: '/services/airbag-srs#booking-form',
},
{
  id: 'coding',
  label: 'Module Coding',
  title: 'Mercedes, BMW & VW group programming',
  body: 'Factory-level ECU, BCM, and body module coding for Mercedes-Benz, BMW, MINI, Audi, VW, Seat, and Skoda. We speak the language of German engineering.',
  tag: 'German Specialists',
  tagColor: '#C8A96E',
  wide: false,
  tall: false,
  dark: true,
  img: null,
  imgAlt: null,
  href: '/services/module-coding',
  buttonHref: '/services/module-coding#booking-form',
},
{
  id: 'bodywork',
  label: 'General Bodywork',
  title: 'Crash repairs, panel beating & painting',
  body: 'From minor dents and stone chips to full panel replacements and colour-matched resprays — every job finished to concours standard.',
  tag: 'All Makes',
  tagColor: '#C8A96E',
  wide: true,
  tall: false,
  dark: false,
  img: "https://img.rocket.new/generatedImages/rocket_gen_img_1b8212412-1773009635956.png",
  imgAlt: 'Auto painter in protective gear applying a fresh coat of silver metallic paint to a car door panel',
  href: '/services/body-repairs',
  buttonHref: '/services/body-repairs#booking-form',
}];


export default function ServicesSection() {
  return (
    <section
      id="services"
      className="relative z-10 bg-[#111111] py-24 md:py-32 border-b border-white/[0.06]"
      aria-label="Services">
      
      <div className="max-w-8xl mx-auto px-6">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-end justify-between mb-14 gap-6">
          <div>
            <h2 className="font-fraunces font-light text-4xl md:text-5xl lg:text-6xl text-white leading-tight">
              Everything your car needs,<br />
              <span className="italic text-[#CC1A1A]">nothing you don&apos;t.</span>
            </h2>
          </div>
          <p className="font-dm-sans text-[15px] text-white/40 max-w-sm leading-relaxed md:text-right">
            From insurance claims to precision factory-level diagnostics — all under one roof.
          </p>
        </div>

        {/* Asymmetric bento grid */}
        <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-6 gap-4">
          {/* Card 1 — Non-fault (wide, 4 cols) */}
          <ServiceCard service={services[0]} className="md:col-span-2 lg:col-span-4 min-h-[320px]" />

          {/* Card 2 — Structural (tall, 2 cols, 2 rows) */}
          <ServiceCard service={services[1]} className="md:col-span-1 lg:col-span-2 lg:row-span-2 min-h-[320px] lg:min-h-[680px]" />

          {/* Card 3 — Airbag */}
          <ServiceCard service={services[2]} className="md:col-span-1 lg:col-span-2 min-h-[280px]" />

          {/* Card 4 — Coding */}
          <ServiceCard service={services[3]} className="md:col-span-1 lg:col-span-2 min-h-[280px]" />

          {/* Card 5 — Bodywork (wide, 4 cols) */}
          <ServiceCard service={services[4]} className="md:col-span-3 lg:col-span-4 min-h-[280px]" />
        </div>
      </div>
    </section>);

}

interface ServiceCardProps {
  service: typeof services[0];
  className?: string;
}

function ServiceCard({ service, className = '' }: ServiceCardProps) {
  const bgClass = service.dark ? 'bg-[#1A1A1A]' : 'bg-[#161616]';
  const router = useRouter();

  return (
    <Link
      href={service.href}
      className={`bento-card reveal-on-scroll hidden-init relative rounded-2xl overflow-hidden border border-white/[0.07] flex flex-col ${bgClass} ${className} group cursor-pointer transition-all duration-300 hover:border-[#C00000]/50 hover:-translate-y-1 hover:shadow-[0_8px_32px_rgba(192,0,0,0.15)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#C00000]`}
      aria-label={`Learn more about ${service.label}`}>
      
      {/* Background image (if present) */}
      {service.img && service.imgAlt &&
      <div className="absolute inset-0 z-0">
          <AppImage
          src={service.img}
          alt={service.imgAlt}
          fill
          sizes="(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 33vw"
          quality={75}
          loading="lazy"
          className="object-cover opacity-20 group-hover:opacity-25 transition-opacity duration-300" />
        
          <div
          className="absolute inset-0"
          style={{ background: 'linear-gradient(to top, rgba(17,17,17,0.98) 0%, rgba(17,17,17,0.6) 60%, rgba(17,17,17,0.3) 100%)' }}
          aria-hidden="true" />
        
        </div>
      }

      {/* Content */}
      <div className="relative z-10 p-7 md:p-8 flex flex-col gap-4 h-full">
        {/* Tag */}
        <div className="flex items-center justify-between">
          <span
            className="inline-block px-3 py-1 rounded-full text-[10px] font-dm-sans font-medium uppercase tracking-[0.2em]"
            style={{ backgroundColor: `${service.tagColor}18`, color: service.tagColor, border: `1px solid ${service.tagColor}30` }}>
            
            {service.tag}
          </span>
          {/* Clickable indicator */}
          <span className="text-[10px] font-dm-sans text-white/20 group-hover:text-white/40 transition-colors hidden sm:block">
            Click to learn more
          </span>
        </div>

        {/* Label */}
        <span className="font-dm-sans text-[10px] font-medium uppercase tracking-[0.25em] text-white/30">
          {service.label}
        </span>

        {/* Title */}
        <h3 className="font-fraunces font-light text-xl md:text-2xl text-white leading-snug group-hover:text-white transition-colors">
          {service.title}
        </h3>

        {/* Body */}
        <p className="font-dm-sans text-[14px] text-white/45 leading-relaxed flex-1">
          {service.body}
        </p>

        {/* Link button — stops propagation so it navigates to buttonHref independently */}
        <div className="mt-auto pt-2 border-t border-white/[0.06]">
          <button
            type="button"
            onClick={(e) => { e.preventDefault(); e.stopPropagation(); router.push(service.buttonHref); }}
            className="inline-flex items-center gap-2 text-[11px] font-dm-sans font-medium uppercase tracking-[0.2em] text-[#C00000] hover:text-[#E02020] transition-colors">
            
            Enquire about this service
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
              <path d="M5 12h14M12 5l7 7-7 7" />
            </svg>
          </button>
        </div>
      </div>
    </Link>);

}